import {
  DEFAULT_BRAND,
  DescriptionList,
  Field,
  Input,
  normaliseHex,
  type Theme,
  themeFromBrand,
} from "@ternary/ui"
import { type CSSProperties, type ReactElement, useState } from "react"

/** The typed colour, spaces around it ignored, as lower-case `#rrggbb`, or null. */
function hex(text: string): string | null {
  return normaliseHex(text.trim())
}

/** What the field says about a colour that is not `#rrggbb`, and the error Save shows for it. */
export const COLOR_FORMAT_MESSAGE = "Enter a colour as #rrggbb, such as #176b51."

/**
 * The brand colours to store for a tenant colour: the original, normalised to `#rrggbb`, as
 * `primaryColor`, so light and dark mode both derive from it at render time; and, as
 * `secondaryColor`, the text colour on the light primary, which legacy pages draw on the primary.
 * Null when `color` is not `#rrggbb` or `#rrggbbaa` (spaces around it are ignored), so a caller
 * never stores the default theme in its place.
 */
export function brandColorsForSave(
  color: string,
): { primaryColor: string; secondaryColor: string } | null {
  const brand = hex(color)
  return brand
    ? { primaryColor: brand, secondaryColor: themeFromBrand(brand).light.onPrimary }
    : null
}

/**
 * What the theme did with the colour, in words, never in hex: buttons darkened when light mode could
 * not put readable text on it, links when it could not use it as link text. Either way the theme it
 * makes is readable in light and dark mode.
 */
function verdict(theme: Theme) {
  const buttons = theme.light.primary !== theme.brand
  const links = theme.light.primaryText !== theme.brand
  if (buttons && links) return "We darkened buttons and links so they stay readable."
  if (buttons) return "We darkened buttons so they stay readable."
  if (links) return "We darkened links so they stay readable."
  return "Readable on light and dark backgrounds."
}

/**
 * The colours the theme uses, shown, not read (the verdict says it in words): buttons and links in
 * light mode, and buttons in dark mode. Each swatch is a box, so no glyph enters the page's text.
 */
function Chips({ theme }: { theme: Theme }) {
  const chips = [
    ["Buttons", theme.light.primary],
    ["Links", theme.light.primaryText],
    ["Dark mode", theme.dark.primary],
  ] as const
  return (
    <span aria-hidden="true" className="admin-color-chips">
      {chips.map(([name, colour]) => (
        <span className="admin-color-chip" key={name}>
          <span
            className="admin-color-chip-swatch"
            style={{ "--admin-chip": colour } as CSSProperties}
          />
          {name}
        </span>
      ))}
    </span>
  )
}

/**
 * A brand colour: its hex as text, with a colour picker in the field's frame for the mouse. Under the text
 * one plain line says what the theme did with it, and chips beside the label show the colours it
 * uses, until an error takes their place. While the text is not a colour, the picker is an empty checkerboard.
 * `onChange` gets each value as typed, so the page can check it on Save.
 * A `#rrggbbaa` value shows as `#rrggbb`. Read-only (`disabled`), it is a value: a swatch and the hex.
 */
export function ColorField({
  label,
  value,
  onChange,
  error,
  disabled = false,
}: {
  label: string
  value: string
  onChange(hex: string): void
  error?: string | undefined
  disabled?: boolean | undefined
}): ReactElement {
  const [text, setText] = useState(() => hex(value) ?? value)
  const [lastValue, setLastValue] = useState(value)
  // The last full colour, which the picker keeps while the text is partial.
  const [shown, setShown] = useState(() => hex(value) ?? DEFAULT_BRAND)
  // A value from outside, rather than this field's own typing, replaces the text.
  if (value !== lastValue) {
    setLastValue(value)
    if (hex(value) !== hex(text)) setText(hex(value) ?? value)
  }

  function change(next: string) {
    setText(next)
    setLastValue(next)
    onChange(next)
  }

  const color = hex(text)
  if (color && color !== shown) setShown(color)
  const theme = color ? themeFromBrand(color) : null
  const hint = theme ? verdict(theme) : COLOR_FORMAT_MESSAGE

  if (disabled) {
    return (
      <DescriptionList
        items={[
          {
            term: label,
            value: (
              <>
                <span
                  aria-hidden="true"
                  className="admin-color-swatch"
                  style={{ backgroundColor: color ?? shown }}
                />
                <span className="admin-mono">{color ?? text}</span>
              </>
            ),
          },
        ]}
      />
    )
  }

  return (
    <div className="admin-color-field">
      {/* The chips sit in the label's row, outside the label, so they add no row of their own. */}
      <Field
        aside={theme && !error ? <Chips theme={theme} /> : undefined}
        error={error}
        hint={hint}
        label={label}
      >
        <Input
          autoComplete="off"
          className="admin-mono"
          maxLength={9}
          onChange={(event) => change(event.currentTarget.value)}
          // The swatch, in the field's frame: a shortcut for the mouse, as the text field is the
          // control everyone can reach.
          prefix={
            <input
              aria-hidden="true"
              className={color ? "admin-color-picker" : "admin-color-picker is-empty"}
              onChange={(event) => change(event.currentTarget.value)}
              tabIndex={-1}
              type="color"
              value={color ?? shown}
            />
          }
          spellCheck={false}
          value={text}
        />
      </Field>
    </div>
  )
}
